import { useCountriesApi, useCountryApi } from '@/api/useCountryApi'
import { shippableCountries } from '@/utils/shippableCountries'
import clsx from 'clsx'
import Button from 'lib/components/Button/Button'
import AutoCompleteInput, {
  AutoCompleteInputProps,
} from 'lib/components/Form/AutoCompleteInput/AutoCompleteInput'
import SelectInput from 'lib/components/Form/SelectInput/SelectInput'
import Notification from 'lib/components/Notification/Notification'
import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { autoCompleteInputRules } from 'lib/utilities/autoCompleteInputUtils'
import {
  hasUnsupportedCountry,
  isRouteUnavailable,
} from 'lib/utilities/bookingWidgetUtils'
import { forwardRef, useCallback, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import {
  BookingWidgetContextData,
  useBookingWidgetStore,
} from '../providers/BookingWidgetContext'

const FALLBACK_SHIPPABLE_COUNTRY_CODES = new Set(
  shippableCountries
    .map(({ countryCode, code }) => (countryCode ?? code)?.toUpperCase())
    .filter((value): value is string => Boolean(value)),
)

export interface BookingWidgetContentProps {
  sticky?: boolean
  onSubmit?: (
    data?: ReturnType<Required<BookingWidgetContextData>['getData']>,
  ) => void
}

const BookingWidgetContent = forwardRef<
  HTMLFormElement,
  BookingWidgetContentProps
>(function BookingWidgetContentWithRef(
  { sticky, onSubmit }: BookingWidgetContentProps,
  ref,
) {
  const autoCompleteInputComboxInputClassNames = clsx([
    '!outline-none',
    '!pr-10',
    '!px-0',
    '!shadow-none',
    '!text-[16px]',
    'border-neutral-400',
    'border-r',
    'focus:outline-none',
    'focus:shadow-none',
    'hover:outline-none',
    'hover:shadow-none',
    'rounded-none',
    'text-ellipsis',
  ])

  const selectInputListboxButtonClassNames = clsx([
    '!outline-none',
    '!px-0',
    '!shadow-none',
    '!text-[16px]',
    '!w-auto',
    'data-[focus]:outline-none',
    'data-[hover]:shadow-none',
    'focus:shadow-none',
    'gap-2',
    'hover:shadow-none',
  ])

  const form = useForm()

  const {
    addresses,
    areAutoCompleteItemsLoading = [],
    autoCompleteItems = [],
    expandedStep,
    getData,
    loadingServiceLevels,
    onInputSearchTermHandlers = [],
    onSelectAutoCompleteItemHandlers = [],
    serviceOptions = [],
    setTripType,
    totalPriceFormatted,
  } = useBookingWidgetStore()

  const { data: countriesData } = useCountriesApi()

  const originCountryCode = addresses?.[0]?.[0]?.country_code
  const destinationCountryCode = addresses?.[0]?.[1]?.country_code

  const { data: originCountry } = useCountryApi(originCountryCode)
  const { data: destinationCountry } = useCountryApi(destinationCountryCode)

  const countryCodes = useMemo(() => {
    const normalizedOrigin = originCountryCode?.toUpperCase()
    const normalizedDestination = destinationCountryCode?.toUpperCase()

    return [normalizedOrigin, normalizedDestination].filter(
      (code): code is string => Boolean(code),
    )
  }, [destinationCountryCode, originCountryCode])

  const hasLegacyUnsupportedCountry = useMemo(
    () => hasUnsupportedCountry(addresses),
    [addresses],
  )

  const shouldShowCountryRestriction = useMemo(() => {
    if (hasLegacyUnsupportedCountry) {
      return true
    }

    if (
      originCountry?.shipEnabled === false ||
      destinationCountry?.shipEnabled === false ||
      originCountry?.publicShippable === false ||
      destinationCountry?.publicShippable === false
    ) {
      return true
    }

    if (!countryCodes.length) {
      return false
    }

    if (Array.isArray(countriesData) && countriesData.length > 0) {
      return countryCodes.some((code) => {
        const country = countriesData.find((entry) => {
          const normalizedCode = entry.code?.toUpperCase()
          const normalizedCountryCode = entry.countryCode?.toUpperCase()

          return normalizedCode === code || normalizedCountryCode === code
        })

        if (!country) {
          return false
        }

        return !(country.publicShippable && country.shipEnabled)
      })
    }

    if (Array.isArray(countriesData) && countriesData.length === 0) {
      return !countryCodes.some((code) =>
        FALLBACK_SHIPPABLE_COUNTRY_CODES.has(code),
      )
    }

    return false
  }, [
    countriesData,
    countryCodes,
    destinationCountry?.publicShippable,
    destinationCountry?.shipEnabled,
    hasLegacyUnsupportedCountry,
    originCountry?.publicShippable,
    originCountry?.shipEnabled,
  ])

  const destination = useWatch({ control: form.control, name: 'destination' })
  const origin = useWatch({ control: form.control, name: 'origin' })

  const canProceed = useMemo(
    () =>
      addresses?.[0]?.every(
        (address) =>
          (!!address?.city && !!address?.country_code && !!address?.state) ||
          !!address?.formatted_address,
      ) &&
      origin &&
      destination &&
      !shouldShowCountryRestriction,
    [addresses, destination, origin, shouldShowCountryRestriction],
  )

  // Check if route is unavailable (no services available or unsupported country)
  // Note: Pass false for hasAttemptedFetch since homepage booking widget doesn't select products
  const routeUnavailable = useMemo(() => {
    // First check if origin or destination is in an unsupported country
    if (shouldShowCountryRestriction) {
      return true
    }

    // Otherwise check if route is unavailable due to no services
    return isRouteUnavailable(
      addresses,
      origin,
      destination,
      serviceOptions,
      loadingServiceLevels,
      false, // Don't show unavailable on homepage - products not selected yet
    )
  }, [
    addresses,
    origin,
    destination,
    serviceOptions,
    loadingServiceLevels,
    shouldShowCountryRestriction,
  ])

  const changeType = useCallback(
    (value?: string | number) => {
      if (value) setTripType?.(value as 'roundtrip' | 'oneway')
    },
    [setTripType],
  )

  const formatLocationValue = useCallback<
    Required<AutoCompleteInputProps>['displayValue']
  >(
    (data) =>
      data?.formatted_address?.replace(/<br>/g, ', ') || data?.name || '',
    [],
  )

  const formatOption = useCallback((item: AutoCompleteItem) => {
    if (!item?.address?.city) return item?.formatted_address || item.name
    // GSE-11976: include address line 1 (street). The mapped autocomplete
    // address holds the street under `address1` (camelToSnakeCase leaves it
    // unchanged), so read it robustly — mirrors src/utils/autoComplete.ts.
    const addr = item.address as { address1?: string; address_1?: string }
    const street = addr.address1 ?? addr.address_1 ?? ''
    return [
      `${item.name} <br>`,
      street ? `${street},` : '',
      `${item.address?.city},`,
      item.address?.state,
      item.address?.zip,
    ]
      .filter(Boolean)
      .join(' ')
  }, [])

  const submit = useCallback(() => {
    onSubmit?.(getData?.())
  }, [getData, onSubmit])

  return (
    <FormProvider {...form}>
      <form
        ref={ref}
        className={clsx(
          'w-full md-max:min-h-full relative mx-auto',
          'lg:mx-auto lg:overflow-visible lg:transition-transform z-10',
          sticky ? '-translate-y-full' : 'max-w-[1440px]',
        )}
        onSubmit={form.handleSubmit(submit)}
      >
        {/* Gray spacer to account for popover extension below the main widget (desktop) */}
        {expandedStep ? (
          <div
            className={clsx(
              'md-max:hidden w-full absolute top-full left-0 right-0 -mt-4 h-10 bg-neutral-200',
            )}
          ></div>
        ) : null}

        <div
          className={clsx(
            'flex flex-col bg-white w-full',
            'lg:relative lg:px-6 lg:py-4 lg:shadow-popover',
            !sticky && 'lg:rounded-16',
            sticky && 'lg:px-[64px] lg:py-2',
          )}
        >
          <div
            className={clsx('flex gap-8 justify-between', 'lg:items-center')}
          >
            <div className="flex-grow md-max:w-full">
              <div
                className={clsx(
                  'flex flex-col',
                  'lg:flex-row lg:gap-4',
                  sticky && '[&]:lg:mt-0',
                )}
              >
                {/* Origin */}
                <AutoCompleteInput
                  busy={areAutoCompleteItemsLoading?.[0]?.[0] || false}
                  childrenClassNames={{
                    ComboboxInput: autoCompleteInputComboxInputClassNames,
                    errorText: 'absolute -bottom-3',
                  }}
                  control={form.control}
                  displayValue={formatLocationValue}
                  name="origin"
                  onChange={onSelectAutoCompleteItemHandlers?.[0]?.[0]}
                  onInput={onInputSearchTermHandlers?.[0]?.[0]}
                  optionFormat={formatOption}
                  options={autoCompleteItems?.[0]?.[0] || []}
                  placeholder="Where from?"
                  autoComplete="off"
                  address={addresses?.[0]?.[0] || null}
                  resetField={form.resetField}
                  rules={autoCompleteInputRules(
                    addresses?.[0]?.[0] || null,
                    addresses?.[0]?.[1] || null,
                  )}
                />
                {/* Destination */}
                <AutoCompleteInput
                  busy={areAutoCompleteItemsLoading?.[0]?.[1] || false}
                  childrenClassNames={{
                    ComboboxInput: autoCompleteInputComboxInputClassNames,
                    errorText: 'absolute -bottom-3',
                  }}
                  control={form.control}
                  displayValue={formatLocationValue}
                  name="destination"
                  onChange={onSelectAutoCompleteItemHandlers?.[0]?.[1]}
                  onInput={onInputSearchTermHandlers?.[0]?.[1]}
                  optionFormat={formatOption}
                  options={autoCompleteItems?.[0]?.[1] || []}
                  placeholder="Where to?"
                  autoComplete="off"
                  address={addresses?.[0]?.[1] || null}
                  resetField={form.resetField}
                  rules={autoCompleteInputRules(
                    addresses?.[0]?.[0] || null,
                    addresses?.[0]?.[1] || null,
                  )}
                />
                {/* Trip type */}
                <SelectInput
                  childrenClassNames={{
                    ListboxButton: selectInputListboxButtonClassNames,
                    ListboxOptions: '!outline-none',
                  }}
                  className="text-body-6-desktop"
                  control={form.control}
                  name="type"
                  onChange={changeType}
                  options={[
                    { label: 'Round trip', value: 'roundtrip' },
                    { label: 'One way', value: 'oneway' },
                  ]}
                  placeholder="Round trip"
                />
              </div>
            </div>

            <div className="md-max:hidden flex items-center gap-4">
              <div className="flex items-baseline gap-2">
                From
                <span className="type-headline-1 text-theme-accessibility">
                  {totalPriceFormatted}
                </span>
                each way
              </div>
              <Button
                disabled={!canProceed}
                type="submit"
                className={clsx(sticky && '[&]:lg:min-h-12')}
              >
                Get started
              </Button>
            </div>
          </div>
          {/* Route unavailable banner - appears below the entire row, full width */}
          {routeUnavailable ? (
            <div className="mt-4 lg:px-0">
              <Notification
                size="small"
                icon={{ icon: 'info-circle-filled' }}
                type="info"
                title="Please contact customer support at 855-867-9915 to complete your booking."
                className="!text-left"
                dismissible
              />
            </div>
          ) : null}
        </div>
      </form>
    </FormProvider>
  )
})

export default BookingWidgetContent
